import { CheckSheetAction, CheckSheetActionTypes, UpdateSignatureMatrixSuccess } from '../../actions/check-sheet.action';
import { CheckListItem, CheckSheetAssignment, UserAssignment } from '@completion/models';
import { CompletionStatus } from '@completion/enums';
export interface CheckSheetHistoryState {
  checkSheetAssignmentId: number;
  assignmentHistory: Array<{
    id: number;
    revision: number;
    status: string;
    checkSheet?: { csNumber?: string };
    company?: { name?: string };
    phase?: { phaseNumber?: string };
  }>;
  itemStatusHistory: Array<{
    revision: number;
    status: string;
    csItemNumber: number;
    name: string;
    enteredTimestamp: string;
    enteredByUserName: string | null;
  }>;
}

export interface CheckSheetState {
  currentCsaId: number;
  currentCsa: CheckSheetAssignment;
  currentCsItems: CheckListItem[];
  matrixTypes: string[];
  history?: CheckSheetHistoryState;
  exportScope: CheckSheetAssignment[];
  mipsPublishResults: { [itemNumber: number]: { published: boolean; error: string | null } };
  mipsPublishInProgress: boolean;
  mipsPublishComplete: boolean;
}

export const initialState: CheckSheetState = {
  currentCsaId: null,
  currentCsa: null,
  currentCsItems: null,
  matrixTypes: [],
  history: null,
  exportScope: [],
  mipsPublishResults: {},
  mipsPublishInProgress: false,
  mipsPublishComplete: false,
};

export function checkSheetReducer(state: CheckSheetState = initialState, action: CheckSheetAction): CheckSheetState {
  switch (action.type) {
    case CheckSheetActionTypes.LoadCsaByIds:
      return {
        ...state,
        exportScope: [],
      };
    case CheckSheetActionTypes.LoadCsaByIdsSuccess:
      return {
        ...state,
        exportScope: action.csas,
      };
    case CheckSheetActionTypes.ChangeCurrentCsaId:
      return {
        ...state,
        currentCsaId: action.csaId,
      };
    case CheckSheetActionTypes.LoadCurrentCsaSuccess:
      return {
        ...state,
        currentCsa: action.csa,
        currentCsItems: action.csa.csItems,
      };
    case CheckSheetActionTypes.ClearCurrentCsa:
      return {
        ...state,
        currentCsa: null,
        currentCsItems: null,
        history: null,
      };
    case CheckSheetActionTypes.UpdateCsItemStatusSuccess:
      return {
        ...state,
        currentCsItems: (state.currentCsItems ?? []).map((i) => (i.number === action.itemNumber ? action.item : i)),
      };
    case CheckSheetActionTypes.PatchCsItem: {
      const p = action.payload;

      // ── helper: build UserAssignment from string fields ──────────────────
      const toUserAssignment = (name: string | null | undefined, ts: string | null | undefined) => {
        if (!name || name === 'NULL') {
          return null;
        }
        return new UserAssignment({ id: null, name, userName: name, email: null, sapId: '' }, ts ? new Date(ts) : new Date());
      };

      // ── patch csItems if item delta present ──────────────────────────────
      let updatedCsItems = state.currentCsItems;
      let updatedCsaItems = state.currentCsa?.csItems;

      if (p.item) {
        const { itemNumber, status, enteredBy, enteredByTimestamp } = p.item;
        const patchItem = (i: CheckListItem) => {
          if (parseFloat(String(i.number)) !== parseFloat(String(itemNumber))) {
            return i;
          }
          return {
            ...i,
            status: status as CompletionStatus,
            ...(enteredBy !== undefined && { enteredBy: toUserAssignment(enteredBy, enteredByTimestamp) }),
          } as CheckListItem;
        };
        updatedCsItems = (state.currentCsItems ?? []).map(patchItem);
        updatedCsaItems = (state.currentCsa?.csItems ?? []).map(patchItem);
      }

      // ── patch CSA-level fields ───────────────────────────────────────────
      if (!state.currentCsa) {
        return { ...state, currentCsItems: updatedCsItems };
      }

      const patchedCsa = {
        ...state.currentCsa,
        csItems: updatedCsaItems,
        ...('assignmentStatus' in p &&
          p.assignmentStatus !== undefined && {
            status: p.assignmentStatus as CompletionStatus,
          }),
        ...('signedBy' in p && {
          signedBy: toUserAssignment(p.signedBy, p.signedByTimestamp),
        }),
        ...('verifiedBy' in p && {
          verifiedBy: toUserAssignment(p.verifiedBy, p.verifiedByTimestamp),
        }),
        ...('comments' in p && {
          comments: p.comments === 'NULL' ? null : p.comments,
        }),
      };

      return { ...state, currentCsItems: updatedCsItems, currentCsa: patchedCsa };
    }
    case CheckSheetActionTypes.UpdateSignaturesSuccess:
      return {
        ...state,
        currentCsa: {
          ...state.currentCsa,
          signedBy: action.csa.signedBy,
          verifiedBy: action.csa.verifiedBy,
        },
      };
    case CheckSheetActionTypes.GetCheckSheetMatrixTypesSuccess:
      return {
        ...state,
        matrixTypes: action.matrixTypes,
      };
    case CheckSheetActionTypes.GetCheckSheetHistorySuccess:
      return {
        ...state,
        history: action.history as any,
      };
    case CheckSheetActionTypes.UpdateSignatureMatrixSuccess: {
      if (!state.currentCsa) {
        return state;
      }
      const signatureAction = action as UpdateSignatureMatrixSuccess;
      return {
        ...state,
        currentCsa: {
          ...state.currentCsa,
          signatureMatrix: signatureAction.matrix,
        },
      };
    }
    case CheckSheetActionTypes.PublishCsItemsToMips:
      return {
        ...state,
        mipsPublishResults: {},
        mipsPublishInProgress: true,
        mipsPublishComplete: false,
      };
    case CheckSheetActionTypes.PublishCsItemToMipsResult:
      return {
        ...state,
        mipsPublishResults: {
          ...state.mipsPublishResults,
          [action.itemNumber]: { published: action.published, error: action.error },
        },
      };
    case CheckSheetActionTypes.PublishCsItemsToMipsDone:
      return {
        ...state,
        mipsPublishInProgress: false,
        mipsPublishComplete: true,
      };
    default:
      return state;
  }
}
